import { useReducer } from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';

interface ResetPasswordUiState {
  password: string;
  confirmPassword: string;
  error: string;
  loading: boolean;
  success: boolean;
}

const initialResetPasswordUi: ResetPasswordUiState = {
  password: '',
  confirmPassword: '',
  error: '',
  loading: false,
  success: false,
};

type ResetPasswordUiAction =
  | { type: 'set-password'; value: string }
  | { type: 'set-confirm-password'; value: string }
  | { type: 'set-error'; message: string }
  | { type: 'clear-error' }
  | { type: 'before-submit' }
  | { type: 'submit-done' }
  | { type: 'submit-success' };

function resetPasswordUiReducer(state: ResetPasswordUiState, action: ResetPasswordUiAction): ResetPasswordUiState {
  switch (action.type) {
    case 'set-password':
      return { ...state, password: action.value };
    case 'set-confirm-password':
      return { ...state, confirmPassword: action.value };
    case 'set-error':
      return { ...state, error: action.message };
    case 'clear-error':
      return { ...state, error: '' };
    case 'before-submit':
      return { ...state, error: '', loading: true };
    case 'submit-done':
      return { ...state, loading: false };
    case 'submit-success':
      return { ...state, success: true, loading: false };
    default:
      return state;
  }
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [ui, dispatch] = useReducer(resetPasswordUiReducer, initialResetPasswordUi);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    dispatch({ type: 'clear-error' });
    if (ui.password !== ui.confirmPassword) {
      dispatch({ type: 'set-error', message: 'Пароли не совпадают' });
      return;
    }
    if (ui.password.length < 8) {
      dispatch({ type: 'set-error', message: 'Пароль должен быть не менее 8 символов' });
      return;
    }
    if (!token) {
      dispatch({ type: 'set-error', message: 'Недействительная ссылка' });
      return;
    }
    dispatch({ type: 'before-submit' });
    try {
      await api.post('/auth/reset-password', { token, password: ui.password });
      dispatch({ type: 'submit-success' });
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      dispatch({
        type: 'set-error',
        message:
          err instanceof Error
            ? err.message
            : 'Не удалось сбросить пароль. Ссылка могла истечь.',
      });
    } finally {
      dispatch({ type: 'submit-done' });
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted p-4">
        <Card className="w-full max-w-md py-10">
          <CardHeader className="text-center px-12">
            <CardTitle className="text-2xl font-bold">Ошибка</CardTitle>
            <CardDescription>
              Недействительная ссылка. Запросите сброс пароля повторно.
            </CardDescription>
          </CardHeader>
          <CardFooter className="px-12">
            <Link to="/forgot-password" className="text-primary hover:underline">
              Запросить сброс пароля
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md py-10">
        <CardHeader className="text-center px-12">
          <CardTitle className="text-2xl font-bold">Новый пароль</CardTitle>
          <CardDescription>
            Введите новый пароль для вашего аккаунта
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-7">
          <CardContent className="flex flex-col gap-4 px-12">
            {ui.error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {ui.error}
              </div>
            )}
            {ui.success ? (
              <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                Пароль успешно изменён. Перенаправление на страницу входа...
              </p>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <label htmlFor="password" className="text-sm font-medium">
                    Новый пароль
                  </label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Минимум 8 символов"
                    value={ui.password}
                    onChange={(e) => dispatch({ type: 'set-password', value: e.target.value })}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="confirmPassword"
                    className="text-sm font-medium"
                  >
                    Подтвердите пароль
                  </label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Повторите пароль"
                    value={ui.confirmPassword}
                    onChange={(e) => dispatch({ type: 'set-confirm-password', value: e.target.value })}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                </div>
              </>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-4 px-12">
            {!ui.success && (
              <Button type="submit" className="w-full" disabled={ui.loading}>
                {ui.loading ? 'Сохраняем...' : 'Сбросить пароль'}
              </Button>
            )}
            <p className="text-sm text-muted-foreground">
              <Link to="/login" className="text-primary hover:underline">
                Вернуться ко входу
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
